// Motion helpers (11 §9.3, §14): one place that knows whether decorative motion is on, plus the
// `Reveal` wrapper and `stagger()` style for list entrances. Durations live in tokens.css; these
// helpers only read the reduced-motion state so JS-timed effects (typewriter, counters, modal
// unmount) agree with the CSS.
import { useEffect, useState, type CSSProperties, type ElementType, type ReactNode } from 'react';
import { useSettings } from '../../core/stores/settings';

/** True when the player's setting or the OS asks for reduced motion. Safe outside React. */
export function motionReduced(): boolean {
  if (typeof document === 'undefined') return false;
  if (document.documentElement.dataset.reduceMotion === 'true') return true;
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** `ms` when motion is on, 0 when it is reduced — for JS timers that mirror CSS durations. */
export const motionMs = (ms: number): number => (motionReduced() ? 0 : ms);

export function useReducedMotion(): boolean {
  const setting = useSettings((s) => s.reduceMotion);
  const [os, setOs] = useState(() => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const mq = matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setOs(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return setting || os;
}

/** Applies the accessibility settings to <html>: UI/text scale custom properties, reduced motion and colour mode attributes. */
export function useUiSettings(): void {
  const uiScaleBp = useSettings((s) => s.uiScaleBp);
  const textSizeBp = useSettings((s) => s.textSizeBp);
  const reduceMotion = useSettings((s) => s.reduceMotion);
  const colourMode = useSettings((s) => s.colourMode);
  useEffect(() => {
    const html = document.documentElement;
    html.style.setProperty('--fk-ui-scale', String(uiScaleBp / 10000));
    html.style.setProperty('--fk-text-scale', String(textSizeBp / 10000));
    html.dataset.reduceMotion = String(reduceMotion);
    html.dataset.colourMode = colourMode;
  }, [uiScaleBp, textSizeBp, reduceMotion, colourMode]);
}

/** Inline style that staggers a `.fk-reveal` / `.fk-card--rise` entrance by list index. */
export const stagger = (index: number): CSSProperties => ({ ['--fk-i' as string]: index } as CSSProperties);

export type RevealKind = 'rise' | 'pop' | 'flip' | 'fade';

/** Wraps children in an entrance animation; `index` staggers siblings. */
export function Reveal({ as: Tag = 'div', kind = 'rise', index = 0, className = '', children, style, ...rest }: {
  as?: ElementType; kind?: RevealKind; index?: number; className?: string; children?: ReactNode; style?: CSSProperties;
} & Record<string, unknown>) {
  return <Tag className={`fk-reveal ${kind === 'rise' ? '' : `fk-reveal--${kind}`} ${className}`} style={{ ...stagger(index), ...style }} {...rest}>{children}</Tag>;
}

/** Runs `fn` after `ms` of motion time (immediately when motion is reduced); returns the cancel. */
export function afterMotion(ms: number, fn: () => void): () => void {
  const t = setTimeout(fn, motionMs(ms));
  return () => clearTimeout(t);
}
